import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Drawer, Form, Input, Space, Switch, Table, Tabs, message } from 'antd';
import { API_BASE_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';

const SemantixSearch = () => {
  const { getAuthHeaders, account } = useAuth();
  const [activeTab, setActiveTab] = useState('server');
  const [cfg, setCfg] = useState({
    solr: { base_url: '', username: '', password: '', verify_ssl: true, shards: 1, replicas: 1 },
  });

  const [schemaSectionTab, setSchemaSectionTab] = useState('fields');
  const [schemaData, setSchemaData] = useState({ client: null });
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [schemaError, setSchemaError] = useState('');
  const [schemaSearch, setSchemaSearch] = useState('');
  const [schemaDrawerOpen, setSchemaDrawerOpen] = useState(false);
  const [schemaDrawerMode, setSchemaDrawerMode] = useState('add');
  const [schemaDrawerType, setSchemaDrawerType] = useState('field');
  const [, setSchemaDrawerRecord] = useState(null);
  const [schemaDrawerSchema, setSchemaDrawerSchema] = useState('ahri');
  const [schemaForm] = Form.useForm();
  const [schemaDrawerWidth, setSchemaDrawerWidth] = useState(() => {
    if (typeof window === 'undefined') return 520;
    return Math.round(window.innerWidth * 0.5);
  });
  const [schemaDrawerResizing, setSchemaDrawerResizing] = useState(false);
  const schemaResizeRef = useRef({ startX: 0, startWidth: 0 });

  const schemaEndpoints = useMemo(
    () => ({
      client: `${API_BASE_URL}/api/v1/ahri/solr/schema`,
    }),
    []
  );

  const onNum = (val, min, max) => {
    const n = parseInt(val || '0', 10);
    if (isNaN(n)) return '';
    let m = n;
    if (typeof min === 'number') m = Math.max(min, m);
    if (typeof max === 'number') m = Math.min(max, m);
    return m;
  };

  const loadConfig = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/v1/ahri/config`, { headers: getAuthHeaders() });
      if (!r.ok) return;
      const j = await r.json();
      const nextCfg = { ...j?.config };
      if (!nextCfg.solr) {
        nextCfg.solr = { base_url: '', username: '', password: '', verify_ssl: true, shards: 1, replicas: 1 };
      }
      setCfg((prev) => ({ ...prev, ...nextCfg }));
    } catch {
      // ignore
    }
  }, [getAuthHeaders]);

  const save = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/v1/ahri/config`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(cfg),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j?.detail || `HTTP ${r.status}`);
      }
      message.success('Saved');
    } catch (err) {
      message.error(String(err?.message || err));
    }
  }, [cfg, getAuthHeaders]);

  const loadSchema = useCallback(
    async (schemaKey) => {
      const endpoint = schemaEndpoints[schemaKey];
      if (!endpoint) return;
      setSchemaLoading(true);
      setSchemaError('');
      try {
        const resp = await fetch(endpoint, { headers: getAuthHeaders() });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data?.detail || `HTTP ${resp.status}`);
        }
        setSchemaData((prev) => ({ ...prev, [schemaKey]: data?.schema || {} }));
      } catch (err) {
        setSchemaError(String(err?.message || err));
      } finally {
        setSchemaLoading(false);
      }
    },
    [schemaEndpoints, getAuthHeaders]
  );

  const openSchemaDrawer = useCallback(
    (schemaKey, type, mode, record = null) => {
      setSchemaDrawerSchema(schemaKey);
      setSchemaDrawerType(type);
      setSchemaDrawerMode(mode);
      setSchemaDrawerRecord(record);
      schemaForm.resetFields();
      if (record) {
        schemaForm.setFieldsValue({
          name: record.name || record.source || '',
          type: record.type || '',
          stored: record.stored,
          indexed: record.indexed,
          multiValued: record.multiValued,
          docValues: record.docValues,
          source: record.source,
          dest: record.dest,
          maxChars: record.maxChars,
          class: record.class,
          json: JSON.stringify(record, null, 2),
        });
      }
      setSchemaDrawerOpen(true);
    },
    [schemaForm]
  );

  const submitSchemaDrawer = useCallback(async () => {
    const values = await schemaForm.validateFields();
    const endpointBase = schemaEndpoints[schemaDrawerSchema];
    if (!endpointBase) return;
    const target = `${endpointBase}/${schemaDrawerType}`;
    const method = schemaDrawerMode === 'edit' ? 'PATCH' : 'POST';
    const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
    let payload = {};
    if (values.json) {
      try {
        payload = JSON.parse(values.json);
      } catch (err) {
        message.error('Invalid JSON in Advanced JSON field.');
        return;
      }
    } else if (schemaDrawerType === 'copy-field') {
      payload = { source: values.source, dest: values.dest };
      if (values.maxChars) payload.maxChars = Number(values.maxChars);
    } else if (schemaDrawerType === 'field-type') {
      payload = { name: values.name, class: values.class };
    } else {
      payload = {
        name: values.name,
        type: values.type,
        stored: !!values.stored,
        indexed: !!values.indexed,
        multiValued: !!values.multiValued,
        docValues: !!values.docValues,
      };
    }
    try {
      const resp = await fetch(target, {
        method,
        headers,
        body: JSON.stringify(payload),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.detail || `HTTP ${resp.status}`);
      }
      message.success('Schema updated');
      setSchemaDrawerOpen(false);
      await loadSchema(schemaDrawerSchema);
    } catch (err) {
      message.error(String(err?.message || err));
    }
  }, [
    schemaForm,
    schemaEndpoints,
    schemaDrawerSchema,
    schemaDrawerType,
    schemaDrawerMode,
    getAuthHeaders,
    loadSchema,
  ]);

  const deleteSchemaItem = useCallback(
    async (schemaKey, type, record) => {
      const endpointBase = schemaEndpoints[schemaKey];
      if (!endpointBase) return;
      let url = `${endpointBase}/${type}`;
      if (type === 'copy-field') {
        url += `?source=${encodeURIComponent(record.source || '')}&dest=${encodeURIComponent(record.dest || '')}`;
      } else {
        const name = record.name || record.source;
        url += `?name=${encodeURIComponent(name || '')}`;
      }
      try {
        const resp = await fetch(url, { method: 'DELETE', headers: getAuthHeaders() });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data?.detail || `HTTP ${resp.status}`);
        }
        message.success('Schema updated');
        await loadSchema(schemaKey);
      } catch (err) {
        message.error(String(err?.message || err));
      }
    },
    [schemaEndpoints, getAuthHeaders, loadSchema]
  );

  const schemaSectionLabels = {
    fields: 'Fields',
    fieldTypes: 'Field Types',
    dynamicFields: 'Dynamic Fields',
    copyFields: 'Copy Fields',
  };

  const getSchemaSectionData = (schemaKey, section) => {
    const schema = schemaData[schemaKey] || {};
    if (section === 'fields') return schema.fields || [];
    if (section === 'fieldTypes') return schema.field_types || [];
    if (section === 'dynamicFields') return schema.dynamic_fields || [];
    if (section === 'copyFields') return schema.copy_fields || [];
    return [];
  };

  const renderSchemaTable = (schemaKey, sectionKey) => {
    const data = getSchemaSectionData(schemaKey, sectionKey);
    const searchTerm = (schemaSearch || '').trim().toLowerCase();
    const filteredData = searchTerm
      ? data.filter((row) => {
          if (!row) return false;
          try {
            return JSON.stringify(row).toLowerCase().includes(searchTerm);
          } catch {
            return false;
          }
        })
      : data;
    const typeMap = {
      fields: 'field',
      fieldTypes: 'field-type',
      dynamicFields: 'dynamic-field',
      copyFields: 'copy-field',
    };
    const columns = [
      { title: 'Name', dataIndex: 'name', key: 'name', render: (_, record) => record.name || record.source || '-' },
      { title: 'Type', dataIndex: 'type', key: 'type', render: (_, record) => record.type || record.class || '-' },
      {
        title: 'Actions',
        key: 'actions',
        render: (_, record) => (
          <Space>
            <Button size="small" onClick={() => openSchemaDrawer(schemaKey, typeMap[sectionKey], 'edit', record)}>
              Edit
            </Button>
            <Button size="small" danger onClick={() => deleteSchemaItem(schemaKey, typeMap[sectionKey], record)}>
              Delete
            </Button>
          </Space>
        ),
      },
    ];
    return (
      <Table
        size="small"
        rowKey={(record) => record.name || `${record.source}-${record.dest}`}
        columns={columns}
        dataSource={filteredData}
        pagination={{ pageSize: 15 }}
      />
    );
  };

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    loadSchema('client');
  }, [loadSchema]);

  useEffect(() => {
    if (!schemaDrawerResizing) return undefined;
    const onMove = (event) => {
      const delta = schemaResizeRef.current.startX - event.clientX;
      const rawWidth = schemaResizeRef.current.startWidth + delta;
      const maxWidth = Math.round(window.innerWidth * 0.75);
      const minWidth = 420;
      setSchemaDrawerWidth(Math.max(minWidth, Math.min(maxWidth, rawWidth)));
    };
    const onUp = () => setSchemaDrawerResizing(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [schemaDrawerResizing]);

  return (
    <div className="container-fluid py-4">
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-3">
        <div>
          <h4 className="mb-1">SemantiX Search</h4>
          <div className="text-muted small">Configure Solr search infrastructure and schema.</div>
        </div>
        <div className="d-flex gap-2">
          <button
            type="button"
            className={`btn ${activeTab === 'server' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setActiveTab('server')}
          >
            Search Server Settings
          </button>
          <button
            type="button"
            className={`btn ${activeTab === 'schema' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setActiveTab('schema')}
          >
            Schema Editor
          </button>
        </div>
      </div>

      {activeTab === 'server' && (
        <div className="card mb-3">
          <div className="card-body">
            <h6 className="mb-3">Solr Connection</h6>
            <div className="row g-3">
              <div className="col-md-8">
                <label className="form-label">Base URL</label>
                <input
                  className="form-control"
                  placeholder="https://solr-example:8983/solr"
                  value={cfg.solr?.base_url || ''}
                  onChange={(e)=>setCfg((prev) => ({ ...prev, solr: { ...(prev.solr||{}), base_url: e.target.value } }))}
                />
                <div className="form-text">Point to the SolrCloud root (typically ends with /solr).</div>
              </div>
              <div className="col-md-4">
                <label className="form-label">Verify SSL</label>
                <div className="form-check form-switch mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="semantix_solr_verify_ssl"
                    checked={cfg.solr?.verify_ssl ?? true}
                    onChange={(e)=>setCfg((prev) => ({ ...prev, solr: { ...(prev.solr||{}), verify_ssl: e.target.checked } }))}
                  />
                  <label className="form-check-label" htmlFor="semantix_solr_verify_ssl">Verify HTTPS certificates</label>
                </div>
              </div>
              <div className="col-md-6">
                <label className="form-label">Username (optional)</label>
                <input
                  className="form-control"
                  value={cfg.solr?.username || ''}
                  onChange={(e)=>setCfg((prev) => ({ ...prev, solr: { ...(prev.solr||{}), username: e.target.value } }))}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Password (optional)</label>
                <input
                  type="password"
                  className="form-control"
                  value={cfg.solr?.password || ''}
                  onChange={(e)=>setCfg((prev) => ({ ...prev, solr: { ...(prev.solr||{}), password: e.target.value } }))}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Shards</label>
                <input
                  type="number"
                  min={1}
                  className="form-control"
                  value={cfg.solr?.shards ?? 1}
                  onChange={(e)=>{
                    const v = onNum(e.target.value, 1);
                    setCfg((prev) => ({ ...prev, solr: { ...(prev.solr||{}), shards: v } }));
                  }}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Replicas</label>
                <input
                  type="number"
                  min={1}
                  className="form-control"
                  value={cfg.solr?.replicas ?? 1}
                  onChange={(e)=>{
                    const v = onNum(e.target.value, 1);
                    setCfg((prev) => ({ ...prev, solr: { ...(prev.solr||{}), replicas: v } }));
                  }}
                />
              </div>
              <div className="col-12 d-flex justify-content-end">
                <button className="btn btn-primary" onClick={save}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'schema' && (
        <div className="card mb-3">
          <div className="card-body">
            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-3">
              <div>
                <h6 className="mb-1">Schema Editor</h6>
                <div className="text-muted small">Manage fields, field types, dynamic fields, and copy fields.</div>
              </div>
              <Space className="w-100 w-lg-auto">
                <Input
                  allowClear
                  placeholder="Search schema fields, types, copy fields..."
                  value={schemaSearch}
                  onChange={(e) => setSchemaSearch(e.target.value)}
                  style={{ minWidth: 240 }}
                />
                <Button onClick={() => loadSchema('client')} loading={schemaLoading}>
                  Reload Schema
                </Button>
              </Space>
            </div>
            {schemaError && <div className="alert alert-danger">{schemaError}</div>}
            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-3">
              <div className="text-muted small">
                Client schema: <code>client_{account?.id || 'account'}</code>
              </div>
              <Button
                type="primary"
                onClick={() =>
                  openSchemaDrawer(
                    'client',
                    schemaSectionTab === 'fieldTypes'
                      ? 'field-type'
                      : schemaSectionTab === 'dynamicFields'
                      ? 'dynamic-field'
                      : schemaSectionTab === 'copyFields'
                      ? 'copy-field'
                      : 'field',
                    'add'
                  )
                }
              >
                Add {schemaSectionLabels[schemaSectionTab]}
              </Button>
            </div>
            <Tabs
              activeKey={schemaSectionTab}
              onChange={(key) => setSchemaSectionTab(key)}
              items={[
                { key: 'fields', label: 'Fields', children: renderSchemaTable('client', 'fields') },
                { key: 'fieldTypes', label: 'Field Types', children: renderSchemaTable('client', 'fieldTypes') },
                { key: 'dynamicFields', label: 'Dynamic Fields', children: renderSchemaTable('client', 'dynamicFields') },
                { key: 'copyFields', label: 'Copy Fields', children: renderSchemaTable('client', 'copyFields') },
              ]}
            />
          </div>
        </div>
      )}

      <Drawer
        title={`${schemaDrawerMode === 'edit' ? 'Edit' : 'Add'} ${schemaDrawerType.replace('-', ' ')}`}
        open={schemaDrawerOpen}
        onClose={() => setSchemaDrawerOpen(false)}
        width={schemaDrawerWidth}
        placement="right"
      >
        <div className="schema-drawer">
          <div
            className="schema-resize-handle"
            onMouseDown={(event) => {
              schemaResizeRef.current = { startX: event.clientX, startWidth: schemaDrawerWidth };
              setSchemaDrawerResizing(true);
            }}
          />
          <Form form={schemaForm} layout="vertical">
            {(schemaDrawerType === 'field' || schemaDrawerType === 'dynamic-field') && (
              <>
                <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
                  <Input />
                </Form.Item>
                <Form.Item name="type" label="Type" rules={[{ required: true, message: 'Type is required' }]}>
                  <Input placeholder="string, text_general, pdouble..." />
                </Form.Item>
                <Space size="large" style={{ marginBottom: 16 }}>
                  <Form.Item name="stored" valuePropName="checked" label="Stored">
                    <Switch />
                  </Form.Item>
                  <Form.Item name="indexed" valuePropName="checked" label="Indexed">
                    <Switch />
                  </Form.Item>
                  <Form.Item name="multiValued" valuePropName="checked" label="MultiValued">
                    <Switch />
                  </Form.Item>
                  <Form.Item name="docValues" valuePropName="checked" label="DocValues">
                    <Switch />
                  </Form.Item>
                </Space>
              </>
            )}
            {schemaDrawerType === 'field-type' && (
              <>
                <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
                  <Input />
                </Form.Item>
                <Form.Item name="class" label="Class" rules={[{ required: true, message: 'Class is required' }]}>
                  <Input placeholder="solr.TextField" />
                </Form.Item>
              </>
            )}
            {schemaDrawerType === 'copy-field' && (
              <>
                <Form.Item name="source" label="Source" rules={[{ required: true, message: 'Source is required' }]}>
                  <Input />
                </Form.Item>
                <Form.Item name="dest" label="Dest" rules={[{ required: true, message: 'Dest is required' }]}>
                  <Input />
                </Form.Item>
                <Form.Item name="maxChars" label="Max chars (optional)">
                  <Input type="number" />
                </Form.Item>
              </>
            )}
            <Form.Item name="json" label="Advanced JSON (optional)">
              <Input.TextArea rows={6} placeholder="Paste full schema JSON to override the form." />
            </Form.Item>
            <Space>
              <Button type="primary" onClick={submitSchemaDrawer}>
                Save
              </Button>
              <Button onClick={() => setSchemaDrawerOpen(false)}>Cancel</Button>
            </Space>
          </Form>
        </div>
      </Drawer>

      <style>{`
        .schema-drawer {
          position: relative;
        }
        .schema-resize-handle {
          position: absolute;
          left: -8px;
          top: 0;
          bottom: 0;
          width: 8px;
          cursor: col-resize;
        }
      `}</style>
    </div>
  );
};

export default SemantixSearch;
